import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, Loader2, PackageX } from "lucide-react";
import { Input } from "../components/ui/input";
import { TrackResult } from "../components/track/TrackResult";
import { useBrand } from "../context/BrandContext";
import { api, errorMessage } from "../lib/api";

const NotFound = ({ tn, b }) => (
  <div className="panel p-10 text-center rise" data-testid="track-not-found">
    <PackageX className="w-12 h-12 text-orange-600 mx-auto" strokeWidth={1.5} />
    <div className="mt-5 font-display font-bold uppercase text-slate-900 text-3xl">No shipment found</div>
    <p className="mt-3 text-slate-600 max-w-md mx-auto">We couldn't find a shipment with tracking number <span className="font-mono text-slate-900">{tn}</span>. Check the number in your confirmation email, or call us at <a href={`tel:${b.phone_tel}`} className="text-brand-600">{b.phone_display}</a>.</p>
  </div>
);

export default function Track() {
  const b = useBrand();
  const { trackingNumber } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(trackingNumber || "");
  const [state, setState] = useState({ status: "idle" });

  useEffect(() => {
    if (!trackingNumber) { setState({ status: "idle" }); return; }
    setInput(trackingNumber);
    setState({ status: "loading" });
    api.get(`/track/${encodeURIComponent(trackingNumber.trim())}`)
      .then((r) => setState({ status: "found", data: r.data }))
      .catch((e) => setState(e?.response?.status === 404 ? { status: "notfound" } : { status: "error", message: errorMessage(e) }));
  }, [trackingNumber]);

  const submit = (e) => {
    e.preventDefault();
    const tn = input.replace(/\s+/g, "").toUpperCase();
    if (tn) navigate(`/track/${tn}`);
  };

  return (
    <section className="relative min-h-[70vh]" data-testid="track-page">
      <div className="absolute inset-x-0 top-0 h-[420px] grid-lines border-b border-slate-200 bg-[#F7F8FA]" />
      <div className="relative container-x py-16 md:py-20 max-w-5xl">
        <span className="eyebrow">Track shipment</span>
        <h1 className="mt-4 font-display font-black uppercase text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">Where's my vehicle?</h1>
        <p className="mt-4 text-slate-600 max-w-xl">Enter the tracking number from your confirmation email (format ET-XXXXXX).</p>
        <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="track-form">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. ET-7K2M9Q" data-testid="track-input-tracking-id"
            className="field-input h-14 text-lg font-mono uppercase tracking-widest flex-1" aria-label="Tracking number" />
          <button type="submit" className="btn-amber h-14 px-8 text-base" data-testid="track-search-button"><Search className="w-4 h-4" />Track</button>
        </form>
        <div className="mt-3 font-mono text-[11px] text-slate-500 tracking-wider">
          <span data-testid="track-format-hint">e.g. ET-7K2M9Q</span>
        </div>
        <div className="mt-12">
          {state.status === "loading" && <div className="flex items-center gap-3 text-slate-600" data-testid="track-loading"><Loader2 className="w-5 h-5 animate-spin text-orange-600" />Looking up shipment…</div>}
          {state.status === "found" && <TrackResult data={state.data} />}
          {state.status === "notfound" && <NotFound tn={trackingNumber} b={b} />}
          {state.status === "error" && <p className="text-red-600" data-testid="track-error">{state.message}</p>}
        </div>
      </div>
    </section>
  );
}
